import { t } from "@hakili/i18n";
import { BANK_BORDER, BANK_FS_BODY, BANK_FS_LEAD, BANK_FS_READ, BANK_TEXT, BANK_TEXT_2 } from "@hakili/ui";
import type { ReactElement } from "react";

import { fill } from "./dossiers.js";

/** One obligation the file carries after the disbursement (`laterObligations` of the file read). */
export interface LaterObligationRow {
  readonly key: string;
  readonly label: string;
  readonly dueDays: number | null;
}

/**
 * D2 frame 49's words for the obligations whose seeded label (`lending.bank_condition_definitions`, a server definition the conditions
 * stage also reads) differs from the drawing. The bank maps the typed code to its own catalogue; the shared definition is not edited.
 */
const DRAWN = new Map<string, string>([["carteGriseMutee", "bank.decaisse.obligationCarteGrise"]]);

const wordsOf = (obligation: LaterObligationRow): string => {
  const drawn = DRAWN.get(obligation.key);
  return drawn === undefined ? obligation.label : t(drawn);
};

/**
 * `ODF-2` (D2 frame 49): the obligations after the disbursement, one card for the conditions stage and the décaissé one, so the
 * two never drift. The rows are the file read's own; a file with none prints no card.
 */
export function ObligationsCard(props: {
  readonly obligations: readonly LaterObligationRow[];
  /**
   * `decaisse`: D2 frame 49's card (« Obligations après le décaissement », a bulleted list, the timing inside each bullet);
   * `apres`: frame 46's (« Après le décaissement »: the same bullets, « annuel » on the renewal, and the help line under them).
   */
  readonly variant: "apres" | "decaisse";
}): ReactElement {
  const bullets = props.obligations.map((obligation) => (
    <li key={obligation.key} className={`${BANK_FS_READ} ${BANK_TEXT}`} data-testid="bank-obligation-puce">
      {`${wordsOf(obligation)}, ${
        obligation.dueDays === null
          ? t(
              props.variant === "apres"
                ? "bank.conditions.renouvellementChaque"
                : "bank.decaisse.obligationRenouvellement",
            )
          : fill("bank.conditions.sousJours", { n: String(obligation.dueDays) })
      }`}
    </li>
  ));
  if (props.variant === "apres") {
    return (
      <section
        className={`mb-4 overflow-hidden rounded-[var(--hk-r-lg)] border bg-[color:var(--hk-surface)] ${BANK_BORDER}`}
        data-testid="bank-conditions-obligations"
      >
        <div className="px-5 pb-1 pt-4">
          <h3 className={`${BANK_FS_LEAD} font-semibold leading-[1.3] ${BANK_TEXT}`}>
            {t("bank.conditions.apres")}
          </h3>
        </div>
        <div className="px-5 pb-[18px] pt-3">
          <ul className="mt-1.5 ml-[18px] grid list-disc gap-1 pl-5">{bullets}</ul>
          <p className={`mt-2 ${BANK_FS_BODY} ${BANK_TEXT_2}`}>{t("bank.conditions.apresAide")}</p>
        </div>
      </section>
    );
  }
  return (
    <div
      className="rounded-2xl border border-gray-200 bg-[color:var(--hk-surface)] px-5 py-4"
      data-testid="bank-conditions-obligations"
    >
      <h3 className={`mb-3 ${BANK_FS_LEAD} font-semibold leading-[1.3] ${BANK_TEXT}`}>
        {t("bank.decaisse.obligations")}
      </h3>
      {/* D2 `.bul`: a disc list, 6 px apart, 20 px in, at the reading size; each item one sentence with its timing. */}
      <ul className="grid list-disc gap-1.5 pl-5">{bullets}</ul>
    </div>
  );
}
